import { TransformDataAssetI } from "../../../services/PlayCanvasService/type";

export interface IRenderConfig {
  stage: IStage;
  camera: ICamera;
  products: IProduct[];
}

export interface IStage {
  id: number;
  name: string;
  paramString: string;
  resourceUrl: string;
  options?: any[];
}

export interface ICamera {
  id: number;
  fov: number;
  name: string;
  enabled: boolean;
  "360steps": ISteps;
  exposure: number | null;
  fontName: string;
  fontSize: number;
  imageType: string;
  lineWidth: number;
  transform: ITransform;
  capsLength: number;
  imageWidth: number;
  lineOffset: number;
  renderType: string;
  imageHeight: number;
  paramString: string;
  renderWidth: number;
  sensorWidth: number;
  antiAliasing: string;
  renderHeight: number;
  renderShadow: boolean;
  renderWarmup: number;
  rotationType: string;
  focalDistance: number;
  fovCorrection: number;
  nearClipPlane: number;
  rotationAngle: number;
  rotationSteps: number;
  backgroundType: string;
  backgroundColor: string;
  dimensionsColor: string;
  renderDimensions: boolean;
}

export interface ISteps {
  total: number;
  current: number;
}

export interface ITransform {
  scale: IVector3;
  position: IVector3;
  rotation: IRotation;
}

export interface IVector3 {
  x: number;
  y: number;
  z: number;
}

export interface IRotation {
  yaw: number;
  roll: number;
  pitch: number;
}

export interface IStudioCategory {
  id: number;
  name: string;
  image?: string;
  projects: IProjectCategory[];
  children: IStudioCategory[];
}

export interface IProjectCategory {
  id: number;
  name: string;
  imgCover?: string;
  image?: string;
  availableProducts: IProduct[];
}

export interface IProduct {
  id: number;
  name: string;
  resource: string;
  paramString: string;
  imgCover?: any;
  glbBundle?: any;
  layeredRender: boolean;
  playcanvas?: any;
  enabled: boolean;
  studio: boolean;
  projectId: number;
  availableOptions: IAvailableOption[];
  availableGeometryOptions: IAvailableGeometryOption[];
  availableStages: IProductAvailableStage[];
}

interface IProductAvailableStage {
  id: number;
  name: string;
  resource: string;
  enabled: boolean;
  paramString: string;
  cameras: ICameraProductStage[];
  options?: any[];
}

export interface IVector3 {
  x: number;
  y: number;
  z: number;
}

export interface IRotation {
  roll: number;
  pitch: number;
  yaw: number;
}

export interface ICameraProductStage {
  id: number;
  name: string;
  position: IVector3;
  rotation: IRotation;
  productRotation: IRotation;
  rotationAngle: number;
  backgroundType: string;
  backgroundColor: string;
  renderShadow: boolean;
  renderDimensions: boolean;
  dimensionsColor: string;
  lineOffset: number;
  lineWidth: number;
  capsLength: number;
  fontSize: number;
  fontName: string;
  fovCorrection: number;
  fov: number;
  rotationSteps: number;
  rotationType: string;
  exposure: number | null;
  nearClipPlane: number;
  renderHeight: number;
  renderWidth: number;
  imageHeight: number;
  imageWidth: number;
  renderType: string;
  imageType: string;
  renderWarmup: number;
  antiAliasing: string;
  paramString: string;
  enabled: boolean;
  order: number;
  "360steps"?: ISteps;
}

export interface IVariant {
  id: number;
  name: string;
  image: string;
  enabled: boolean;
}

export interface IOption {
  id: number;
  name: string;
  resource: string;
  paramString: string;
  variants: IVariant[];
  image: string;
}

export interface IAvailableOption {
  id: number;
  proxyName: string;
  dependencies: any[];
  layerOrder: number;
  shadow: number;
  shadowEnabled: boolean;
  enabled: boolean;
  options: IOption[];
}

interface IGeometryDependency {
  id: number;
  type: string;
}

export interface IAvailableGeometryOption {
  id: number;
  proxyName: string;
  dependencies: IGeometryDependency[];
  layerOrder: number;
  shadow: number;
  shadowEnabled: boolean;
  enabled: boolean;
  options: IGeometryOption[];
}

export interface IStudioStage {
  id: number;
  name: string;
  resource: string;
  image: string;
  enabled: boolean;
  paramString: string;
  availableOptions: IMaterialProxyOption[];
  availableGeometryOptions: IGeometryProxyOption[];
}

export interface IProjectCategoriesResponseData {
  data: any[];
}

export interface IProjectConfigurationResponseData {
  rows: IStudioStage[];
}

export interface IObject extends IStudioCategory {
  parent?: IObject | null;
}

export interface IStageResponseData {
  count: number;
  rows: IStudioStage[];
}

export interface IMaterialProxyOption {
  id: number;
  proxyName: string;
  enabled: boolean;
  materialOptions: IMaterialOption[];
}

export interface IMaterialOption {
  id: number;
  name: string;
  resource: string | null;
  paramString: string;
  variants: IMaterialVariant[];
}

export interface IMaterialVariant {
  id: number;
  name: string;
  image: string;
  enabled: boolean;
}

export interface IGeometryProxyOption {
  id: number;
  proxyName: string;
  enabled: boolean;
  geometryOptions: IGeometryOption[];
}

export interface IGeometryOption {
  id: number;
  name: string;
  paramString: string;
  variants: IGeometryVariant[];
  image: string;
}

export interface IGeometryVariant {
  id: number;
  name: string;
  image: string;
  enabled: boolean;
}

export interface ProductGLBDataI {
  url: string;
  transform: TransformDataAssetI;
}

export type SelectedObjectData = Record<string, ISelectItemObjectData>; // id product ui -> SelectObjectData

export interface ISelectItemObjectData {
  projectId: number; // project id of the product
  realId: number; // real id of the product
  pathObjectIds: number[]; // path to the object via folder names
  materialOptions: ISelectObjectOptions; // options for the object
  geometryOptions: ISelectObjectOptions; // geometry options for the object

  previewImage?: string; // preview image for the object

  glbData?: ProductGLBDataI; // GLB data for the object
}

export interface ISelectObjectOptions {
  [idProxy: number]: {
    [idOption: number]: number; // idOption -> idVariant
  };
}

export type optionObjectOptionT = "material" | "geometry";

export const checkMaterialOption = (
  option: IGeometryOption | IOption
): option is IOption => {
  return "resource" in option;
};

export interface IObjectProcessData {
  imageHash: string | null; // hash of the image
  prevProductUiId: string | null; // id of the previous product
  uiProductLoadId: string | null; // id of the product that is currently being loaded
  renderGLBHash: string | null; // hash of the GLB file being rendered

  tempSelectData: ISelectItemObjectData | null; // temporary select data for the object being processed
}

export interface ITemplateFoldersResponseData {
  count: number;
  rows: IObject[];
}

export interface ICreateTemplateFolderRequest {
  name: string;
}

export interface ICreateTemplateFolderResponseData {
  folder: IObject;
}

/**
 * Template data returned by GET /templates?folderId=<folderId>
 */
export interface ITemplate {
  id: number;
  name: string;
  image?: string | null;
  userId: number;
  folderId: number;
  config?: string; // JSON string or object
  createdAt: string;
  updatedAt: string;
}

/**
 * API response with pagination support
 */
export interface ITemplatesResponseData {
  count: number;
  rows: ITemplate[];
}

/** Request payload for creating a new template */
export interface ICreateTemplateRequest {
  name: string;
  folderId: number;
  config: any; // scene configuration object or string
  image: string | null; // base64 or URL
}

export interface ISearchData {
  searchValue: string; // The current search value
  searchFolders: IObject[]; // Array of objects matching the search criteria
  searchProjects: SearchProjectI[]; // Array of products matching the search criteria
  searchProducts?: IProduct[]; // Products returned from server-side search
}

// Search API (projects) response typings
export interface ISearchProjectRow {
  id: number;
  name: string;
  image?: string;
  studio?: boolean;
  studioCategoryId?: number;
  organizationId?: number;
  // Some backends include renderStats, createdAt, updatedAt; mark as optional
  renderStats?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface ISearchProjectsResponseData {
  count: number;
  rows: ISearchProjectRow[];
}

export interface SearchProjectI {
  project: IProjectCategory;
  pathToProject: number[];
}
